// src/components/RelatieDossierPopup.js
// vDOS-005
//
// Centraal, read-only relatiedossier.
// Toont onderliggende gegevens en opent de bestaande bronprogramma's
// met de gekozen relatie of registratie geselecteerd.

import React, { useEffect, useMemo, useState } from "react";

const VERSION = "vDOS-005";

function safeText(value, fallback = "") {
  if (value === null || value === undefined) return fallback;
  return String(value);
}

function safeTrim(value) {
  return safeText(value).trim();
}

function isoDate(value) {
  const text = safeText(value).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(text) ? text : "";
}

function nlDate(value) {
  const date = isoDate(value);
  if (!date) return "";
  const [year, month, day] = date.split("-");
  return `${day}-${month}-${year}`;
}

function nlDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return nlDate(value) || safeText(value);

  return date.toLocaleString("nl-NL", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function euro(value) {
  const number = Number(value || 0);
  if (!Number.isFinite(number)) return "€ 0,00";

  return number.toLocaleString("nl-NL", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function relationName(row = {}) {
  const entityType = safeText(row.relation_entity_type || "company").toLowerCase();

  if (entityType === "private") {
    const normal = [
      row.first_name || row.initials,
      row.name_prefix,
      row.last_name,
    ].map(safeTrim).filter(Boolean).join(" ");

    return safeTrim(row.search_name || normal || row.name);
  }

  return safeTrim(row.search_name || row.name);
}

function contactName(row = {}) {
  return safeTrim(
    row.search_name ||
    row.full_name ||
    [row.first_name, row.name_prefix, row.last_name]
      .map(safeTrim)
      .filter(Boolean)
      .join(" ")
  );
}

function noteType(row = {}) {
  const value = safeText(row.kind || row.type).toLowerCase();
  return value === "task" ? "Taak" : "Notitie";
}

function noteStatus(row = {}) {
  const value = safeText(row.status).toLowerCase();
  if (value === "done") return "Afgerond";
  return value ? value.charAt(0).toUpperCase() + value.slice(1) : "";
}

function offerStatus(row = {}) {
  const value = safeText(row.status || "OFFERTE").toUpperCase();
  if (value === "FACTUUR") return "Factuur";
  if (value === "OPDRACHT") return "Opdracht";
  return "Offerte";
}

function workOrderStatus(row = {}) {
  const value = safeText(row.completion_status).toUpperCase();
  if (value === "COMPLETED") return "Opgeleverd";
  if (value === "PENDING_APPROVAL") return "Ter akkoord";
  return "Niet opgeleverd";
}

function invoiceStatus(row = {}) {
  const value = safeText(row.status).toUpperCase();
  if (value === "PAID" || value === "BETAALD") return "Betaald";
  if (value === "PARTIAL" || value === "DEELBETALING") return "Deelbetaling";
  if (value === "FINAL" || value === "GEFACTUREERD") return "Gefactureerd";
  return "Concept";
}

function processStatus(row = {}) {
  const value = safeText(row.process_status).toUpperCase();
  if (value === "COMPLETED") return "Afgerond";
  if (value === "ACTION_REQUIRED") return "Actie nodig";
  return "Lopend";
}

function activeLabel(value) {
  return value === false ? "Nee" : "Ja";
}

function hrmStatus(value) {
  const text = safeTrim(value);
  return text || "-";
}

function decimalNl(value, decimals = 2) {
  if (value === null || value === undefined || value === "") return "-";

  const number = Number(String(value).replace(",", "."));
  if (!Number.isFinite(number)) return safeText(value) || "-";

  return number.toLocaleString("nl-NL", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function buildHeaders() {
  let user = {};
  try {
    user = JSON.parse(
      sessionStorage.getItem("user") ||
      localStorage.getItem("user") ||
      "{}"
    );
  } catch {}

  const headers = {
    "Content-Type": "application/json",
    "x-tenant":
      sessionStorage.getItem("tenant") ||
      localStorage.getItem("tenant") ||
      "demo",
    "x-role": user?.role || "user",
    "x-staffiq-user-id": user?.id || "",
    "x-staffiq-user-email": user?.email || "",
    "x-staffiq-user-name": user?.display_name || user?.name || "",
    "x-company-code-ids":
      sessionStorage.getItem("staffiq_company_code_ids") ||
      localStorage.getItem("staffiq_company_code_ids") ||
      "",
  };

  const token =
    sessionStorage.getItem("token") ||
    localStorage.getItem("token") ||
    "";

  if (token) headers.Authorization = `Bearer ${token}`;

  Object.keys(headers).forEach((key) => {
    if (!headers[key]) delete headers[key];
  });

  return headers;
}

async function fetchJson(url) {
  const response = await fetch(url, {
    headers: buildHeaders(),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data?.error || `${response.status} ${response.statusText}`);
  }

  return data;
}

async function loadCommunicationSettingsSafe() {
  try {
    const data = await fetchJson("/api/communication-settings");

    return {
      email_enabled: data?.email_enabled !== false,
      whatsapp_enabled: data?.whatsapp_enabled !== false,
      sms_enabled: data?.sms_enabled === true,
      default_country_code: data?.default_country_code || "+31",
      default_sms_text: data?.default_sms_text || "",
      default_whatsapp_text: data?.default_whatsapp_text || "",
    };
  } catch {
    return {
      email_enabled: true,
      whatsapp_enabled: true,
      sms_enabled: false,
      default_country_code: "+31",
      default_sms_text: "",
      default_whatsapp_text: "",
    };
  }
}

function normalizePhoneForCommunication(raw, defaultCountryCode = "+31") {
  let value = safeTrim(raw);

  if (!value || value === "-") return "";

  value = value
    .replace(/[^\d+]/g, "")
    .replace(/^00/, "+");

  if (!value) return "";
  if (value.startsWith("+")) return value;
  if (value.startsWith("0")) return `${defaultCountryCode}${value.slice(1)}`;
  if (value.startsWith("31")) return `+${value}`;

  return `${defaultCountryCode}${value}`;
}

function callPhone(phone) {
  const value = safeTrim(phone);
  if (!value || value === "-") return;
  window.location.href = `tel:${value}`;
}

async function sendEmail(email) {
  const value = safeTrim(email);
  if (!value || value === "-") return;

  const settings = await loadCommunicationSettingsSafe();

  if (settings.email_enabled === false) {
    alert("E-mail is niet actief in Communicatie-instellingen.");
    return;
  }

  window.location.href = `mailto:${encodeURIComponent(value)}`;
}

async function handleMobileCommunication(mobile) {
  const raw = safeTrim(mobile);
  if (!raw || raw === "-") return;

  const settings = await loadCommunicationSettingsSafe();
  const normalized = normalizePhoneForCommunication(
    raw,
    settings.default_country_code || "+31"
  );

  if (!normalized) return;

  const choice = window.prompt(
    `Wat wil je doen met ${raw}?\n\n1 = Bellen\n2 = SMS\n3 = WhatsApp`,
    "3"
  );

  if (choice === null) return;

  const selected = safeTrim(choice).toLowerCase();

  if (selected === "1" || selected === "bellen" || selected === "bel") {
    window.location.href = `tel:${raw}`;
    return;
  }

  if (selected === "2" || selected === "sms") {
    if (settings.sms_enabled !== true) {
      alert("SMS is niet actief in Communicatie-instellingen.");
      return;
    }

    const message = window.prompt(
      `SMS naar ${raw}`,
      settings.default_sms_text || ""
    );

    if (message === null) return;
    if (!safeTrim(message)) {
      alert("SMS tekst ontbreekt.");
      return;
    }

    const response = await fetch("/api/communications/sms", {
      method: "POST",
      headers: buildHeaders(),
      body: JSON.stringify({
        to: raw,
        text: safeTrim(message),
      }),
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      alert(data?.error || "SMS verzenden mislukt.");
      return;
    }

    alert(`SMS verstuurd naar ${raw}`);
    return;
  }

  if (selected === "3" || selected === "whatsapp" || selected === "app") {
    if (settings.whatsapp_enabled === false) {
      alert("WhatsApp is niet actief in Communicatie-instellingen.");
      return;
    }

    const number = normalized.replace(/[^\d]/g, "");
    const message = safeTrim(settings.default_whatsapp_text);
    const suffix = message ? `?text=${encodeURIComponent(message)}` : "";

    window.open(
      `https://wa.me/${number}${suffix}`,
      "_blank",
      "noopener,noreferrer"
    );
  }
}

function CommunicationValue({ type, value }) {
  const clean = safeTrim(value);

  if (!clean || clean === "-") return <span>-</span>;

  const onClick =
    type === "email"
      ? () => sendEmail(clean)
      : type === "mobile"
        ? () => handleMobileCommunication(clean)
        : () => callPhone(clean);

  const title =
    type === "email"
      ? `E-mailen: ${clean}`
      : type === "mobile"
        ? `Bellen / SMS / WhatsApp: ${clean}`
        : `Bellen: ${clean}`;

  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      style={{
        border: "none",
        background: "transparent",
        color: "#1f4e79",
        textDecoration: "underline",
        padding: 0,
        cursor: "pointer",
        font: "inherit",
        textAlign: "left",
      }}
    >
      {clean}
    </button>
  );
}

function storeAndOpen(key, value, path) {
  try {
    if (key && value !== null && value !== undefined && value !== "") {
      sessionStorage.setItem(key, String(value));
    }
  } catch {}

  window.location.href = path;
}

function openFiltered(path, organisationId) {
  const separator = path.includes("?") ? "&" : "?";
  window.location.href = `${path}${separator}organisation_id=${encodeURIComponent(
    organisationId
  )}`;
}

function Section({
  title,
  count,
  children,
  onOpenAll,
  openAllLabel = "Alles openen",
  initiallyOpen = false,
}) {
  const [open, setOpen] = useState(initiallyOpen);

  return (
    <section
      style={{
        border: "1px solid #d9e2ec",
        borderRadius: 8,
        background: "white",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) auto auto",
          gap: 8,
          alignItems: "center",
          padding: "10px 12px",
          background: "#f7fafc",
          borderBottom: open ? "1px solid #d9e2ec" : "none",
        }}
      >
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          style={{
            border: "none",
            background: "transparent",
            padding: 0,
            textAlign: "left",
            cursor: "pointer",
            font: "inherit",
            fontWeight: 700,
            color: "#1f4e79",
            minWidth: 0,
          }}
        >
          {open ? "▼" : "▶"} {title}
        </button>

        <span
          style={{
            minWidth: 34,
            textAlign: "center",
            padding: "3px 7px",
            borderRadius: 999,
            background: "#e6f0ff",
            color: "#1f4e79",
            fontWeight: 700,
            fontSize: 12,
          }}
        >
          {Number(count || 0)}
        </span>

        {onOpenAll ? (
          <button
            type="button"
            onClick={onOpenAll}
            style={{
              padding: "6px 9px",
              border: "1px solid #d9d9d9",
              borderRadius: 5,
              background: "white",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {openAllLabel}
          </button>
        ) : (
          <span />
        )}
      </div>

      {open && <div style={{ padding: 12 }}>{children}</div>}
    </section>
  );
}

function EmptyText({ children = "Geen gegevens gevonden." }) {
  return <div style={{ color: "#777", fontSize: 13 }}>{children}</div>;
}

function TableWrap({ children, minWidth = 760 }) {
  return (
    <div
      style={{
        overflowX: "auto",
        border: "1px solid #eceff2",
        borderRadius: 6,
      }}
    >
      <div style={{ minWidth }}>{children}</div>
    </div>
  );
}

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: 13,
};

const thStyle = {
  textAlign: "left",
  padding: "7px 8px",
  borderBottom: "2px solid #d9e2ec",
  background: "#fafafa",
  whiteSpace: "nowrap",
};

const tdStyle = {
  padding: "7px 8px",
  borderBottom: "1px solid #eceff2",
  verticalAlign: "top",
};

const linkButtonStyle = {
  border: "none",
  background: "transparent",
  color: "#1f4e79",
  textDecoration: "underline",
  padding: 0,
  cursor: "pointer",
  font: "inherit",
  whiteSpace: "nowrap",
};

export default function RelatieDossierPopup({
  organisationId,
  staffiqProfile = "ALL",
  activeModule = "CRM",
  onClose,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const isMobile = window.innerWidth <= 768;
  const normalizedProfile = safeText(staffiqProfile || "ALL").toUpperCase();
  const normalizedModule = safeText(activeModule || "CRM").toUpperCase();
  const showMkb =
    normalizedProfile === "MKB" ||
    normalizedModule === "MKB" ||
    normalizedProfile === "ALL";
  const showHrm =
    normalizedProfile === "HRM" ||
    normalizedModule === "HRM" ||
    normalizedProfile === "ALL";

  async function loadDossier() {
    try {
      setLoading(true);
      setError("");

      const dossier = await fetchJson(
        `/api/organisations/${encodeURIComponent(organisationId)}/dossier`
      );

      setData(dossier && typeof dossier === "object" ? dossier : {});
    } catch (err) {
      setError(err?.message || "Dossier laden mislukt.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadDossier();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organisationId]);

  const organisation = data?.organisation || {};
  const contacts = Array.isArray(data?.contacts) ? data.contacts : [];
  const notesTasks = Array.isArray(data?.notes_tasks) ? data.notes_tasks : [];
  const offers = Array.isArray(data?.offers) ? data.offers : [];
  const workOrders = Array.isArray(data?.work_orders) ? data.work_orders : [];
  const invoices = Array.isArray(data?.invoices) ? data.invoices : [];
  const process = Array.isArray(data?.process) ? data.process : [];
  const applications = Array.isArray(data?.applications) ? data.applications : [];
  const vacancies = Array.isArray(data?.vacancies) ? data.vacancies : [];
  const placements = Array.isArray(data?.placements) ? data.placements : [];
  const documents = Array.isArray(data?.documents) ? data.documents : [];

  const title = relationName(organisation) || `Relatie ${organisationId}`;

  const address = useMemo(() => {
    const visit = [
      [organisation.visit_street, organisation.visit_house_number, organisation.visit_addition]
        .map(safeTrim).filter(Boolean).join(" "),
      [organisation.visit_postcode, organisation.visit_city]
        .map(safeTrim).filter(Boolean).join(" "),
      organisation.visit_country,
    ].map(safeTrim).filter(Boolean);

    return visit.join(", ");
  }, [organisation]);

  function printDossier() {
    const printable = document.getElementById("staffiq-relation-dossier-print");
    if (!printable) return;

    const win = window.open("", "_blank", "width=1000,height=800");
    if (!win) {
      alert("Printvenster kon niet worden geopend. Controleer popupblokkering.");
      return;
    }

    win.document.open();
    win.document.write(`
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Dossier ${title}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 22px; color: #222; font-size: 12px; }
            h1 { font-size: 22px; margin: 0 0 16px; }
            h2 { font-size: 16px; color: #1f4e79; margin: 20px 0 7px; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 14px; }
            th { text-align: left; border-bottom: 2px solid #555; padding: 5px; }
            td { border-bottom: 1px solid #ddd; padding: 5px; vertical-align: top; }
            button { display: none !important; }
            section { break-inside: avoid; margin-bottom: 12px; }
          </style>
        </head>
        <body>${printable.innerHTML}</body>
      </html>
    `);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 250);
  }

  async function openDocument(row) {
    if (!row?.id) return;

    if (!row?.storage_path) {
      alert("Aan dit document is geen bestand gekoppeld.");
      return;
    }

    try {
      const response = await fetch(
        `/api/documents/${encodeURIComponent(row.id)}/open`,
        {
          headers: buildHeaders(),
        }
      );

      if (!response.ok) {
        const message = await response.text().catch(() => "");
        throw new Error(message || "Document openen mislukt.");
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const contentType = safeText(response.headers.get("content-type")).toLowerCase();
      const fileName = safeTrim(row.file_name) || `document-${row.id}`;
      const isPdf =
        contentType.includes("pdf") ||
        fileName.toLowerCase().endsWith(".pdf");

      if (isPdf && window.innerWidth > 768) {
        const newTab = window.open("", "_blank");

        if (!newTab) {
          window.URL.revokeObjectURL(blobUrl);
          alert("Document kon niet worden geopend. Controleer popupblokkering.");
          return;
        }

        newTab.document.title = fileName;
        newTab.document.body.style.margin = "0";
        newTab.document.body.style.height = "100vh";

        const frame = newTab.document.createElement("iframe");
        frame.src = blobUrl;
        frame.style.border = "0";
        frame.style.width = "100%";
        frame.style.height = "100%";

        newTab.document.body.appendChild(frame);

        window.setTimeout(() => {
          try {
            window.URL.revokeObjectURL(blobUrl);
          } catch {}
        }, 60000);

        return;
      }

      const anchor = document.createElement("a");
      anchor.href = blobUrl;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();

      window.setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 2000);
    } catch (err) {
      alert(err?.message || "Document openen mislukt.");
    }
  }

  const toolbarButton = {
    padding: "8px 11px",
    border: "1px solid #d9d9d9",
    borderRadius: 5,
    background: "white",
    cursor: "pointer",
    whiteSpace: "nowrap",
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100000,
        background: "rgba(0,0,0,0.42)",
        padding: isMobile ? 7 : 18,
        boxSizing: "border-box",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        onClick={(event) => event.stopPropagation()}
        style={{
          width: "min(1260px, 100%)",
          height: isMobile ? "96vh" : "92vh",
          background: "#fff",
          borderRadius: 12,
          boxShadow: "0 22px 70px rgba(0,0,0,0.32)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "minmax(0, 1fr) auto",
            gap: 10,
            alignItems: "center",
            padding: isMobile ? 10 : 14,
            borderBottom: "1px solid #dfe5ea",
            background: "#f8fafc",
          }}
        >
          <div style={{ minWidth: 0 }}>
            <h2 style={{ margin: 0, color: "#1f4e79" }}>
              Dossier Relatie{" "}
              <span style={{ fontSize: 12, opacity: 0.55 }}>{VERSION}</span>
            </h2>
            <div
              style={{
                marginTop: 3,
                fontSize: 14,
                fontWeight: 700,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {title}
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr 1fr 1fr" : "auto auto auto",
              gap: 7,
            }}
          >
            <button type="button" onClick={loadDossier} style={toolbarButton}>
              🔄 Vernieuw
            </button>
            <button type="button" onClick={printDossier} style={toolbarButton}>
              🖨 Printen
            </button>
            <button type="button" onClick={onClose} style={toolbarButton}>
              Sluiten
            </button>
          </div>
        </div>

        <div
          id="staffiq-relation-dossier-print"
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            padding: isMobile ? 9 : 14,
            background: "#f5f7f9",
          }}
        >
          {loading && <div>Dossier laden…</div>}

          {error && (
            <div
              style={{
                padding: 12,
                border: "1px solid #f0b7b7",
                background: "#fff0f0",
                color: "#9b1c1c",
                borderRadius: 7,
              }}
            >
              {error}
            </div>
          )}

          {!loading && !error && (
            <div style={{ display: "grid", gap: 12 }}>
              <Section
                title="Algemene gegevens"
                count={1}
                initiallyOpen
                onOpenAll={() => onClose?.()}
                openAllLabel="Sluiten"
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile
                      ? "1fr"
                      : "repeat(3, minmax(0, 1fr))",
                    columnGap: 18,
                    rowGap: 0,
                    fontSize: 13,
                    fontFamily: "inherit",
                  }}
                >
                  {[
                    ["Naam", title, ""],
                    ["Relatievorm", safeText(organisation.relation_entity_type).toLowerCase() === "private" ? "Particulier" : "Organisatie", ""],
                    ["Soort relatie", organisation.type, ""],
                    ["Administratie", organisation.administration_type, ""],
                    ["Bedrijfscode", organisation.company_code_name || organisation.company_code, ""],
                    ["Datum eerste contact", nlDate(organisation.first_contact_date), ""],
                    ["Telefoon", organisation.phone, "phone"],
                    ["Mobiel", organisation.mobile, "mobile"],
                    ["E-mail", organisation.email, "email"],
                    ["Website", organisation.website, ""],
                    ["Bezoekadres", address, ""],
                    ["KvK", organisation.kvk, ""],
                    ["BTW-nummer", organisation.vat_number, ""],
                    ["IBAN", organisation.iban, ""],
                    ["Betalingsconditie", organisation.payment_terms, ""],
                    ["Betaalwijze", organisation.payment_method, ""],
                    ["Betaaltermijn", organisation.payment_term_days ? `${organisation.payment_term_days} dagen` : "", ""],
                    ["Actief", organisation.active === false ? "Nee" : "Ja", ""],
                  ].map(([label, value, communicationType]) => (
                    <div
                      key={label}
                      style={{
                        display: "grid",
                        gridTemplateColumns: isMobile ? "115px minmax(0, 1fr)" : "125px minmax(0, 1fr)",
                        gap: 8,
                        alignItems: "start",
                        minWidth: 0,
                        padding: "5px 2px",
                        borderBottom: "1px solid #eceff2",
                        lineHeight: 1.35,
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 700,
                          color: "#444",
                          minWidth: 0,
                        }}
                      >
                        {label}
                      </div>

                      <div
                        style={{
                          minWidth: 0,
                          whiteSpace: "pre-wrap",
                          overflowWrap: "anywhere",
                          color: "#222",
                        }}
                      >
                        {communicationType ? (
                          <CommunicationValue type={communicationType} value={value} />
                        ) : (
                          safeTrim(value) || "-"
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {safeTrim(organisation.remarks) && (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isMobile ? "115px minmax(0, 1fr)" : "125px minmax(0, 1fr)",
                      gap: 8,
                      marginTop: 0,
                      padding: "5px 2px",
                      borderBottom: "1px solid #eceff2",
                      fontSize: 13,
                      fontFamily: "inherit",
                      lineHeight: 1.35,
                    }}
                  >
                    <div style={{ fontWeight: 700, color: "#444" }}>
                      Opmerking
                    </div>
                    <div
                      style={{
                        whiteSpace: "pre-wrap",
                        overflowWrap: "anywhere",
                        color: "#222",
                      }}
                    >
                      {organisation.remarks}
                    </div>
                  </div>
                )}
              </Section>

              <Section
                title="Contactpersonen"
                count={contacts.length}
                initiallyOpen
                onOpenAll={() => openFiltered("/contactpersonen", organisationId)}
                openAllLabel="Alle contactpersonen"
              >
                {!contacts.length ? (
                  <EmptyText />
                ) : (
                  <TableWrap minWidth={760}>
                    <table style={tableStyle}>
                      <thead>
                        <tr>
                          <th style={thStyle}>Naam</th>
                          <th style={thStyle}>Afdeling</th>
                          <th style={thStyle}>Functie</th>
                          <th style={thStyle}>Telefoon</th>
                          <th style={thStyle}>Mobiel</th>
                          <th style={thStyle}>E-mail</th>
                          <th style={thStyle}>Actief</th>
                          <th style={thStyle}>Openen</th>
                        </tr>
                      </thead>
                      <tbody>
                        {contacts.map((row) => (
                          <tr key={row.id}>
                            <td style={tdStyle}>{contactName(row) || "-"}</td>
                            <td style={tdStyle}>{row.department || "-"}</td>
                            <td style={tdStyle}>{row.job_function || "-"}</td>
                            <td style={tdStyle}>
                              <CommunicationValue type="phone" value={row.phone} />
                            </td>
                            <td style={tdStyle}>
                              <CommunicationValue type="mobile" value={row.mobile} />
                            </td>
                            <td style={tdStyle}>
                              <CommunicationValue type="email" value={row.email} />
                            </td>
                            <td style={tdStyle}>{row.active === false ? "Nee" : "Ja"}</td>
                            <td style={tdStyle}>
                              <button
                                type="button"
                                style={linkButtonStyle}
                                onClick={() =>
                                  storeAndOpen(
                                    "staffiq_reopen_contact_id",
                                    row.id,
                                    `/contactpersonen?organisation_id=${encodeURIComponent(organisationId)}`
                                  )
                                }
                              >
                                Open
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </TableWrap>
                )}
              </Section>

              <Section
                title="Notities / Taken"
                count={notesTasks.length}
                initiallyOpen
                onOpenAll={() => openFiltered("/notities-taken", organisationId)}
                openAllLabel="Alle notities/taken"
              >
                {!notesTasks.length ? (
                  <EmptyText />
                ) : (
                  <TableWrap minWidth={900}>
                    <table style={tableStyle}>
                      <thead>
                        <tr>
                          <th style={thStyle}>Soort</th>
                          <th style={thStyle}>Status</th>
                          <th style={thStyle}>Vervaldatum</th>
                          <th style={thStyle}>Titel</th>
                          <th style={thStyle}>Uitvoerder</th>
                          <th style={thStyle}>Aangemaakt door</th>
                          <th style={thStyle}>Tekst</th>
                          <th style={thStyle}>Openen</th>
                        </tr>
                      </thead>
                      <tbody>
                        {notesTasks.map((row) => (
                          <tr key={row.id}>
                            <td style={tdStyle}>{noteType(row)}</td>
                            <td style={tdStyle}>{noteStatus(row) || "-"}</td>
                            <td style={tdStyle}>
                              {nlDate(row.due_date) || "-"}{" "}
                              {safeTrim(row.due_time) && safeTrim(row.due_time) !== "00:00"
                                ? safeTrim(row.due_time).slice(0, 5)
                                : ""}
                            </td>
                            <td style={tdStyle}>{row.subject || row.title || "-"}</td>
                            <td style={tdStyle}>{row.assigned_user_name || "-"}</td>
                            <td style={tdStyle}>{row.created_by_user_name || "-"}</td>
                            <td style={{ ...tdStyle, whiteSpace: "pre-wrap", minWidth: 240 }}>
                              {row.body || "-"}
                            </td>
                            <td style={tdStyle}>
                              <button
                                type="button"
                                style={linkButtonStyle}
                                onClick={() =>
                                  storeAndOpen(
                                    "staffiq_reopen_note_task_id",
                                    row.id,
                                    `/notities-taken?organisation_id=${encodeURIComponent(organisationId)}`
                                  )
                                }
                              >
                                Open
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </TableWrap>
                )}
              </Section>

              {showMkb && (
                <>
                  <Section
                    title="Offertes / Opdrachten"
                    count={offers.length}
                    initiallyOpen
                    onOpenAll={() => openFiltered("/offertes", organisationId)}
                    openAllLabel="Alle offertes/opdrachten"
                  >
                    {!offers.length ? (
                      <EmptyText />
                    ) : (
                      <TableWrap minWidth={850}>
                        <table style={tableStyle}>
                          <thead>
                            <tr>
                              <th style={thStyle}>Referentie</th>
                              <th style={thStyle}>Datum</th>
                              <th style={thStyle}>Status</th>
                              <th style={thStyle}>Oplevering</th>
                              <th style={{ ...thStyle, textAlign: "right" }}>Totaal</th>
                              <th style={thStyle}>Openen</th>
                            </tr>
                          </thead>
                          <tbody>
                            {offers.map((row) => (
                              <tr key={row.id}>
                                <td style={tdStyle}>{row.dossier_number || row.offer_number || row.id}</td>
                                <td style={tdStyle}>{nlDate(row.dossier_date || row.offer_date) || "-"}</td>
                                <td style={tdStyle}>{offerStatus(row)}</td>
                                <td style={tdStyle}>
                                  {safeText(row.delivery_status).toUpperCase() === "DELIVERED"
                                    ? "Opgeleverd"
                                    : "Niet opgeleverd"}
                                </td>
                                <td style={{ ...tdStyle, textAlign: "right" }}>
                                  {euro(row.dossier_total || row.total)}
                                </td>
                                <td style={tdStyle}>
                                  <button
                                    type="button"
                                    style={linkButtonStyle}
                                    onClick={() =>
                                      storeAndOpen(
                                        "staffiq_reopen_offer_id",
                                        row.id,
                                        "/offertes"
                                      )
                                    }
                                  >
                                    Open
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </TableWrap>
                    )}
                  </Section>

                  <Section
                    title="Werkbonnen / Opleveringen"
                    count={workOrders.length}
                    onOpenAll={() => openFiltered("/werkbonnen", organisationId)}
                    openAllLabel="Alle werkbonnen"
                  >
                    {!workOrders.length ? (
                      <EmptyText />
                    ) : (
                      <TableWrap minWidth={900}>
                        <table style={tableStyle}>
                          <thead>
                            <tr>
                              <th style={thStyle}>Opdracht</th>
                              <th style={thStyle}>Uitvoering</th>
                              <th style={thStyle}>Uitgevoerd door</th>
                              <th style={thStyle}>Status</th>
                              <th style={thStyle}>Akkoord</th>
                              <th style={thStyle}>Opleverdatum</th>
                              <th style={thStyle}>Acties</th>
                            </tr>
                          </thead>
                          <tbody>
                            {workOrders.map((row) => (
                              <tr key={row.id}>
                                <td style={tdStyle}>{row.offer_number || row.offer_id}</td>
                                <td style={tdStyle}>
                                  {nlDate(row.execution_date) || "-"}{" "}
                                  {row.start_time || ""}
                                </td>
                                <td style={tdStyle}>{row.executed_by || "-"}</td>
                                <td style={tdStyle}>{workOrderStatus(row)}</td>
                                <td style={tdStyle}>{row.approval_status || "-"}</td>
                                <td style={tdStyle}>{nlDate(row.completion_date) || "-"}</td>
                                <td style={tdStyle}>
                                  <button
                                    type="button"
                                    style={linkButtonStyle}
                                    onClick={() =>
                                      storeAndOpen(
                                        "staffiq_reopen_offer_id",
                                        row.offer_id,
                                        `/werkbon-oplevering/${row.offer_id}`
                                      )
                                    }
                                  >
                                    Werkbon
                                  </button>
                                  {row.document_id ? (
                                    <>
                                      {" · "}
                                      <button
                                        type="button"
                                        style={linkButtonStyle}
                                        onClick={() =>
                                          window.open(
                                            `/api/documents/${encodeURIComponent(row.document_id)}/open`,
                                            "_blank",
                                            "noopener,noreferrer"
                                          )
                                        }
                                      >
                                        PDF
                                      </button>
                                    </>
                                  ) : null}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </TableWrap>
                    )}
                  </Section>

                  <Section
                    title="Facturen"
                    count={invoices.length}
                    initiallyOpen
                    onOpenAll={() => openFiltered("/facturen", organisationId)}
                    openAllLabel="Alle facturen"
                  >
                    {!invoices.length ? (
                      <EmptyText />
                    ) : (
                      <TableWrap minWidth={900}>
                        <table style={tableStyle}>
                          <thead>
                            <tr>
                              <th style={thStyle}>Factuurnummer</th>
                              <th style={thStyle}>Factuurdatum</th>
                              <th style={thStyle}>Vervaldatum</th>
                              <th style={thStyle}>Status</th>
                              <th style={{ ...thStyle, textAlign: "right" }}>Totaal</th>
                              <th style={{ ...thStyle, textAlign: "right" }}>Betaald</th>
                              <th style={{ ...thStyle, textAlign: "right" }}>Openstaand</th>
                              <th style={thStyle}>Openen</th>
                            </tr>
                          </thead>
                          <tbody>
                            {invoices.map((row) => (
                              <tr key={row.id}>
                                <td style={tdStyle}>{row.invoice_number || row.id}</td>
                                <td style={tdStyle}>{nlDate(row.invoice_date) || "-"}</td>
                                <td style={tdStyle}>{nlDate(row.due_date) || "-"}</td>
                                <td style={tdStyle}>{invoiceStatus(row)}</td>
                                <td style={{ ...tdStyle, textAlign: "right" }}>
                                  {euro(row.dossier_total || row.total)}
                                </td>
                                <td style={{ ...tdStyle, textAlign: "right" }}>
                                  {euro(row.paid_total)}
                                </td>
                                <td style={{ ...tdStyle, textAlign: "right" }}>
                                  {euro(row.outstanding_amount)}
                                </td>
                                <td style={tdStyle}>
                                  <button
                                    type="button"
                                    style={linkButtonStyle}
                                    onClick={() =>
                                      storeAndOpen(
                                        "staffiq_reopen_invoice_id",
                                        row.id,
                                        "/facturen"
                                      )
                                    }
                                  >
                                    Open
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </TableWrap>
                    )}
                  </Section>

                  <Section
                    title="Opdrachtvoortgang"
                    count={process.length}
                    onOpenAll={() => openFiltered("/opdrachtvoortgang", organisationId)}
                    openAllLabel="Volledige voortgang"
                  >
                    {!process.length ? (
                      <EmptyText />
                    ) : (
                      <TableWrap minWidth={1050}>
                        <table style={tableStyle}>
                          <thead>
                            <tr>
                              <th style={thStyle}>Referentie</th>
                              <th style={thStyle}>Offerte</th>
                              <th style={thStyle}>Opdracht</th>
                              <th style={thStyle}>Uitvoering</th>
                              <th style={thStyle}>Werkbon</th>
                              <th style={thStyle}>Factuur</th>
                              <th style={thStyle}>Betaling</th>
                              <th style={thStyle}>Proces</th>
                              <th style={thStyle}>Openen</th>
                            </tr>
                          </thead>
                          <tbody>
                            {process.map((row) => (
                              <tr key={row.offer_id}>
                                <td style={tdStyle}>
                                  {row.offer_number || row.offer_id}
                                </td>
                                <td style={tdStyle}>{offerStatus({ status: row.offer_status })}</td>
                                <td style={tdStyle}>{row.order_approval_status || "-"}</td>
                                <td style={tdStyle}>
                                  {nlDate(row.execution_date) || "-"} {row.start_time || ""}
                                </td>
                                <td style={tdStyle}>{row.work_order_status || "Niet gemaakt"}</td>
                                <td style={tdStyle}>
                                  {row.invoice_number
                                    ? `${row.invoice_number} · ${invoiceStatus({ status: row.invoice_status })}`
                                    : "Niet gemaakt"}
                                </td>
                                <td style={tdStyle}>{row.payment_status || "-"}</td>
                                <td style={tdStyle}>{processStatus(row)}</td>
                                <td style={tdStyle}>
                                  <button
                                    type="button"
                                    style={linkButtonStyle}
                                    onClick={() =>
                                      storeAndOpen(
                                        "staffiq_reopen_offer_id",
                                        row.offer_id,
                                        "/offertes"
                                      )
                                    }
                                  >
                                    Open
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </TableWrap>
                    )}
                  </Section>
                </>
              )}

              {showHrm && (
                <>
                  <Section
                    title="Aanvragen"
                    count={applications.length}
                    initiallyOpen
                    onOpenAll={() => openFiltered("/aanvragen", organisationId)}
                    openAllLabel="Alle aanvragen"
                  >
                    {!applications.length ? (
                      <EmptyText />
                    ) : (
                      <TableWrap minWidth={1050}>
                        <table style={tableStyle}>
                          <thead>
                            <tr>
                              <th style={thStyle}>Datum</th>
                              <th style={thStyle}>Nummer</th>
                              <th style={thStyle}>Aanvraag</th>
                              <th style={thStyle}>Functie</th>
                              <th style={thStyle}>Aantal</th>
                              <th style={thStyle}>Status</th>
                              <th style={thStyle}>Periode</th>
                              <th style={thStyle}>Plaats / regio</th>
                              <th style={{ ...thStyle, textAlign: "right" }}>Verkooptarief</th>
                              <th style={thStyle}>Bedrijfscode</th>
                              <th style={thStyle}>Openen</th>
                            </tr>
                          </thead>
                          <tbody>
                            {applications.map((row) => (
                              <tr key={row.id}>
                                <td style={tdStyle}>{nlDate(row.application_date) || "-"}</td>
                                <td style={tdStyle}>{row.application_number || row.id}</td>
                                <td style={tdStyle}>{row.title || "-"}</td>
                                <td style={tdStyle}>{row.job_title || "-"}</td>
                                <td style={tdStyle}>{row.number_of_people ?? "-"}</td>
                                <td style={tdStyle}>{hrmStatus(row.status)}</td>
                                <td style={tdStyle}>
                                  {[nlDate(row.start_date), nlDate(row.end_date)]
                                    .filter(Boolean)
                                    .join(" t/m ") || "-"}
                                </td>
                                <td style={tdStyle}>
                                  {[row.place || row.location, row.region]
                                    .map(safeTrim)
                                    .filter(Boolean)
                                    .join(" / ") || "-"}
                                </td>
                                <td style={{ ...tdStyle, textAlign: "right" }}>
                                  {row.sales_rate === null || row.sales_rate === undefined || row.sales_rate === ""
                                    ? "-"
                                    : euro(row.sales_rate)}
                                </td>
                                <td style={tdStyle}>{row.company_code_name || row.company_code || "-"}</td>
                                <td style={tdStyle}>
                                  <button
                                    type="button"
                                    style={linkButtonStyle}
                                    onClick={() =>
                                      storeAndOpen(
                                        "staffiq_reopen_application_id",
                                        row.id,
                                        `/aanvragen?organisation_id=${encodeURIComponent(organisationId)}`
                                      )
                                    }
                                  >
                                    Open
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </TableWrap>
                    )}
                  </Section>

                  <Section
                    title="Vacatures"
                    count={vacancies.length}
                    initiallyOpen
                    onOpenAll={() => openFiltered("/vacatures", organisationId)}
                    openAllLabel="Alle vacatures"
                  >
                    {!vacancies.length ? (
                      <EmptyText />
                    ) : (
                      <TableWrap minWidth={1120}>
                        <table style={tableStyle}>
                          <thead>
                            <tr>
                              <th style={thStyle}>Datum</th>
                              <th style={thStyle}>Nummer</th>
                              <th style={thStyle}>Vacature</th>
                              <th style={thStyle}>Functie</th>
                              <th style={thStyle}>Gevraagd</th>
                              <th style={thStyle}>Voorgesteld</th>
                              <th style={thStyle}>Geplaatst</th>
                              <th style={thStyle}>Status</th>
                              <th style={thStyle}>Periode</th>
                              <th style={thStyle}>Plaats / regio</th>
                              <th style={thStyle}>Bedrijfscode</th>
                              <th style={thStyle}>Openen</th>
                            </tr>
                          </thead>
                          <tbody>
                            {vacancies.map((row) => (
                              <tr key={row.id}>
                                <td style={tdStyle}>{nlDate(row.vacancy_date) || "-"}</td>
                                <td style={tdStyle}>{row.vacancy_number || row.id}</td>
                                <td style={tdStyle}>{row.title || "-"}</td>
                                <td style={tdStyle}>{row.job_title || "-"}</td>
                                <td style={tdStyle}>{row.number_requested ?? "-"}</td>
                                <td style={tdStyle}>{row.number_proposed ?? "-"}</td>
                                <td style={tdStyle}>{row.number_placed ?? "-"}</td>
                                <td style={tdStyle}>{hrmStatus(row.status)}</td>
                                <td style={tdStyle}>
                                  {[nlDate(row.start_date), nlDate(row.end_date)]
                                    .filter(Boolean)
                                    .join(" t/m ") || "-"}
                                </td>
                                <td style={tdStyle}>
                                  {[row.place || row.location, row.region]
                                    .map(safeTrim)
                                    .filter(Boolean)
                                    .join(" / ") || "-"}
                                </td>
                                <td style={tdStyle}>{row.company_code_name || row.company_code || "-"}</td>
                                <td style={tdStyle}>
                                  <button
                                    type="button"
                                    style={linkButtonStyle}
                                    onClick={() =>
                                      storeAndOpen(
                                        "staffiq_reopen_vacancy_id",
                                        row.id,
                                        `/vacatures?organisation_id=${encodeURIComponent(organisationId)}`
                                      )
                                    }
                                  >
                                    Open
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </TableWrap>
                    )}
                  </Section>

                  <Section
                    title="Plaatsingen"
                    count={placements.length}
                    initiallyOpen
                    onOpenAll={() => openFiltered("/plaatsingen", organisationId)}
                    openAllLabel="Alle plaatsingen"
                  >
                    {!placements.length ? (
                      <EmptyText />
                    ) : (
                      <TableWrap minWidth={1180}>
                        <table style={tableStyle}>
                          <thead>
                            <tr>
                              <th style={thStyle}>Datum</th>
                              <th style={thStyle}>Nummer</th>
                              <th style={thStyle}>Personeelslid</th>
                              <th style={thStyle}>Functie</th>
                              <th style={thStyle}>Periode</th>
                              <th style={thStyle}>Uren/week</th>
                              <th style={thStyle}>Status</th>
                              <th style={thStyle}>Kandidaat</th>
                              <th style={thStyle}>Klant</th>
                              <th style={thStyle}>Plaats / regio</th>
                              <th style={thStyle}>Bedrijfscode</th>
                              <th style={thStyle}>Openen</th>
                            </tr>
                          </thead>
                          <tbody>
                            {placements.map((row) => (
                              <tr key={row.id}>
                                <td style={tdStyle}>{nlDate(row.placement_date) || "-"}</td>
                                <td style={tdStyle}>{row.placement_number || row.id}</td>
                                <td style={tdStyle}>{row.personnel_name || "-"}</td>
                                <td style={tdStyle}>{row.job_title || "-"}</td>
                                <td style={tdStyle}>
                                  {[nlDate(row.start_date), nlDate(row.end_date)]
                                    .filter(Boolean)
                                    .join(" t/m ") || "-"}
                                </td>
                                <td style={tdStyle}>{decimalNl(row.hours_per_week)}</td>
                                <td style={tdStyle}>{hrmStatus(row.status)}</td>
                                <td style={tdStyle}>{hrmStatus(row.candidate_status)}</td>
                                <td style={tdStyle}>{hrmStatus(row.customer_status)}</td>
                                <td style={tdStyle}>
                                  {[row.place || row.location, row.region]
                                    .map(safeTrim)
                                    .filter(Boolean)
                                    .join(" / ") || "-"}
                                </td>
                                <td style={tdStyle}>{row.company_code_name || row.company_code || "-"}</td>
                                <td style={tdStyle}>
                                  <button
                                    type="button"
                                    style={linkButtonStyle}
                                    onClick={() =>
                                      storeAndOpen(
                                        "staffiq_reopen_placement_id",
                                        row.id,
                                        `/plaatsingen?organisation_id=${encodeURIComponent(organisationId)}`
                                      )
                                    }
                                  >
                                    Open
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </TableWrap>
                    )}
                  </Section>
                </>
              )}

              <Section
                title="Documenten"
                count={documents.length}
                initiallyOpen
                onOpenAll={() => openFiltered("/documenten", organisationId)}
                openAllLabel="Alle documenten"
              >
                {!documents.length ? (
                  <EmptyText />
                ) : (
                  <TableWrap minWidth={900}>
                    <table style={tableStyle}>
                      <thead>
                        <tr>
                          <th style={thStyle}>Datum</th>
                          <th style={thStyle}>Type</th>
                          <th style={thStyle}>Bestandsnaam</th>
                          <th style={thStyle}>Omschrijving</th>
                          <th style={thStyle}>Contactpersoon</th>
                          <th style={thStyle}>Openen</th>
                        </tr>
                      </thead>
                      <tbody>
                        {documents.map((row) => (
                          <tr key={row.id}>
                            <td style={tdStyle}>
                              {nlDate(row.document_date) ||
                                nlDateTime(row.created_at) ||
                                "-"}
                            </td>
                            <td style={tdStyle}>{row.document_type || "-"}</td>
                            <td style={tdStyle}>{row.file_name || "-"}</td>
                            <td style={{ ...tdStyle, whiteSpace: "pre-wrap" }}>
                              {row.description || "-"}
                            </td>
                            <td style={tdStyle}>{row.contact_name || "-"}</td>
                            <td style={tdStyle}>
                              {row.storage_path ? (
                                <button
                                  type="button"
                                  style={linkButtonStyle}
                                  onClick={() => openDocument(row)}
                                >
                                  Open
                                </button>
                              ) : (
                                "-"
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </TableWrap>
                )}
              </Section>

            </div>
          )}
        </div>
      </div>
    </div>
  );
}
